import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, Skeleton, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { IS_SA, SOUQ_BANK_DETAILS } from '../../../config/appConfig';
import { LanguageService } from '../../../services/languageService';
import { TextWrapper } from '../formUtilities/useTextWrapper';
import { UserService } from '../../../services/userService';
import { useEffect, useState } from 'react';
import { InvestorService } from '../../../pages/investor/investorService';
import { getUser } from '../../../services/authService';
import { Roles } from '../../../constants/roles';

export const FSBankDetails = ({ virtualIban }) => {
	const { t } = useTranslation();
	const languageService = new LanguageService();
	const investorService = new InvestorService();
	const userService = new UserService();
	const [iban, setIban] = useState("");
	const [loading, setLoading] = useState(IS_SA);
	const [investorDetails, setInvestorDetails] = useState(null);

	const copyItem = (text, classList) => {
		let copyText = document.querySelector(`.${classList}`);
		navigator.clipboard.writeText(text);
		copyText.classList.add('active');
		setTimeout(function () {
			copyText.classList.remove('active');
		}, 500);
	};

	useEffect(() => {
		if (IS_SA) {
			getKSADetails();
		} else {
			setIban(SOUQ_BANK_DETAILS.souqIBAN);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [virtualIban]);

	const getKSADetails = async () => {
		try {
			setLoading(true);

			// Check if the user role is Investor
			const user = getUser();
			const promises = [];

			if (user?.role === Roles.Investor) {
				promises.push(investorService.getInvestor());
			} else {
				promises.push(Promise.resolve(null)); // Prevents Promise.all from failing
			}

			promises.push(virtualIban !== undefined ? Promise.resolve({ data: virtualIban }) : userService.getVirtualIban());

			const [investorData, virtualIbanData] = await Promise.all(promises);

			if (investorData) {
				setInvestorDetails(investorData.data);
			}

			setIban(virtualIbanData.data);
		} catch (err) {
			console.error("Error fetching data:", err);
		} finally {
			setLoading(false);
		}
	};

	const getAccountNumber = () => {
		if (IS_SA && iban) {
			return iban.slice(-15);
		}
		return SOUQ_BANK_DETAILS.souqAccountNumber;
	};


	const getAccountName = () => {
		if (IS_SA && investorDetails && getUser()?.role === Roles.Investor) {
			return `${investorDetails?.firstName} ${investorDetails?.lastName}`;
		}
		return SOUQ_BANK_DETAILS.souqAccountName;
	};

	return (
		<Stack
			gap={2}
			className={languageService.isArabic() ? 'arabic-bank-details' : ''}>
			<TextWrapper
				classList="!text-lg !font-[700]"
				text={t('fundingSouqBankDetails')}></TextWrapper>
			<Box className="grid grid-cols-3 items-center">
				<TextWrapper
					classList="!caption !text-sm"
					text={t('bankName')}></TextWrapper>
				<TextWrapper
					classList="!font-bold !text-sm !col-span-2 !mx-2"
					text={
						<Box
							className="flex items-center cursor-pointer copy-button bank-name"
							onClick={() => {
								copyItem(SOUQ_BANK_DETAILS.souqBankName, 'bank-name');
							}}>
							<ContentCopyIcon className="!text-[16px]"></ContentCopyIcon>
							<span className="mx-1">{SOUQ_BANK_DETAILS.souqBankName}</span>
						</Box>
					}></TextWrapper>
			</Box>
			<Box className="grid grid-cols-3 items-center">
				<TextWrapper
					classList="!caption !text-sm"
					text={t('bankAddress')}></TextWrapper>
				<TextWrapper
					classList="!font-bold !text-sm !col-span-2 !mx-2"
					text={
						<Box
							className="flex items-center cursor-pointer copy-button bank-address"
							onClick={() => {
								copyItem(SOUQ_BANK_DETAILS.souqBankAddress, 'bank-address');
							}}>
							<ContentCopyIcon className="!text-[16px]"></ContentCopyIcon>
							<span className="mx-1">{SOUQ_BANK_DETAILS.souqBankAddress}</span>
						</Box>
					}></TextWrapper>
			</Box>
			<Box className="grid grid-cols-3 items-center">
				<TextWrapper
					classList="!caption !text-sm"
					text={t('accountName')}></TextWrapper>
				<TextWrapper
					classList="!font-bold !text-sm !col-span-2 !mx-2"
					text={
						<Box
							className="flex items-center cursor-pointer copy-button account-name"
							onClick={() => {
								copyItem(getAccountName(), 'account-name');
							}}>
							{loading ? <Skeleton variant="text" sx={{ fontSize: '10px', width: '100px' }} /> : <>
								<ContentCopyIcon className="!text-[16px]"></ContentCopyIcon>
								<span className="mx-1">{getAccountName()}</span></>}

						</Box>
					}></TextWrapper>
			</Box>
			<Box className="grid grid-cols-3 items-center">
				<TextWrapper
					classList="!caption !text-sm"
					text={t('accountNo')}></TextWrapper>
				<TextWrapper
					classList="!font-bold !text-sm !col-span-2 !mx-2"
					text={
						<Box
							className="flex items-center cursor-pointer copy-button account-number"
							onClick={() => {
								copyItem(getAccountNumber(), 'account-number');
							}}>
							{loading ? <Skeleton variant="text" sx={{ fontSize: '10px', width: '100px' }} /> : <>
								<ContentCopyIcon className="!text-[16px]"></ContentCopyIcon>
								<span className="mx-1">{getAccountNumber()}</span></>}
						</Box>
					}></TextWrapper>
			</Box>
			<Box className="grid grid-cols-3 items-center">
				<TextWrapper
					classList="!caption !text-sm"
					text={t('iban')}></TextWrapper>
				<TextWrapper
					classList="!font-bold !text-sm !col-span-2 !mx-2"
					text={
						<Box
							className="flex items-center cursor-pointer copy-button iban"
							onClick={() => {
								copyItem(iban, 'iban');
							}}>
							{!loading ? <>
								{iban ? <>
									<ContentCopyIcon className="!text-[16px]"></ContentCopyIcon>
									<span className="mx-1">{iban}</span>
								</> : null}
							</> : <Skeleton variant="text" sx={{ fontSize: '10px', width: '100px' }} />}

						</Box>
					}></TextWrapper>
			</Box>
			<Box className="grid grid-cols-3 items-center">
				<TextWrapper
					classList="!caption !text-sm"
					text={t('swiftCode')}></TextWrapper>
				<TextWrapper
					classList="!font-bold !text-sm !col-span-2 !mx-2"
					text={
						<Box
							className="flex items-center cursor-pointer copy-button swift-code"
							onClick={() => {
								copyItem(SOUQ_BANK_DETAILS.souqSwiftCode, 'swift-code');
							}}>
							<ContentCopyIcon className="!text-[16px]"></ContentCopyIcon>
							<span className="mx-1">{SOUQ_BANK_DETAILS.souqSwiftCode}</span>
						</Box>
					}></TextWrapper>
			</Box>
			{/* {!IS_SA ? <Box className="grid grid-cols-3 items-center">
				<TextWrapper
					classList="!caption !text-sm"
					text={t('abaRoutingNumber')}></TextWrapper>
				<TextWrapper
					classList="!font-bold !text-sm !col-span-2 !mx-2"
					text={
						<Box
							className="flex items-center cursor-pointer copy-button aba-routing-number"
							onClick={() => {
								copyItem(SOUQ_BANK_DETAILS.abaRoutingNumber, 'aba-routing-number');
							}}>
							<ContentCopyIcon className="!text-[16px]"></ContentCopyIcon>
							<span className="mx-1">{SOUQ_BANK_DETAILS.abaRoutingNumber}</span>
						</Box>
					}></TextWrapper>
			</Box> : null} */}

		</Stack>
	);
};
